export type QcChecklistItemCategory = 'general' | 'laboratory';

export type QcChecklistItemType = 'pass_fail' | 'numeric' | 'text' | 'boolean' | 'lab_test';

export interface QcLabTestValue {
  sampleReference: string;
  method: string;
  resultValue: string;
  unit: string;
  referenceRange: string;
  labReportReference: string;
  testedAt: string | null;
}

export interface QcChecklistItemDef {
  id: string;
  label: string;
  required?: boolean;
  type: QcChecklistItemType;
  category?: QcChecklistItemCategory;
  unit?: string;
  minValue?: number;
  maxValue?: number;
  methodHintKey?: string;
}

export interface QcChecklistTemplate {
  version: 1;
  items: QcChecklistItemDef[];
}

export type QcChecklistOutcome = 'pass' | 'fail' | 'na' | '';

export interface QcChecklistItemResult {
  id: string;
  label: string;
  type: QcChecklistItemType;
  category: QcChecklistItemCategory;
  required: boolean;
  unit?: string;
  minValue?: number;
  maxValue?: number;
  methodHintKey?: string;
  outcome: QcChecklistOutcome;
  value?: number | boolean | string | QcLabTestValue | null;
  notes: string;
}

export interface QcChecklistResults {
  version: 1;
  ruleId: string | null;
  ruleCode: string | null;
  items: QcChecklistItemResult[];
}

export interface QcChecklistDefaultItem {
  id: string;
  labelKey: string;
  required: boolean;
  type: QcChecklistItemType;
  category: QcChecklistItemCategory;
  unit?: string;
  methodHintKey?: string;
}

export const DEFAULT_QC_GENERAL_ITEMS: QcChecklistDefaultItem[] = [
  { id: 'visual', labelKey: 'catalog.qc.checklist.default.visual', required: true, type: 'pass_fail', category: 'general' },
  { id: 'packaging', labelKey: 'catalog.qc.checklist.default.packaging', required: true, type: 'pass_fail', category: 'general' },
  { id: 'documentation', labelKey: 'catalog.qc.checklist.default.documentation', required: false, type: 'pass_fail', category: 'general' },
  { id: 'quantity', labelKey: 'catalog.qc.checklist.default.quantity', required: true, type: 'pass_fail', category: 'general' },
  { id: 'measurement', labelKey: 'catalog.qc.checklist.default.measurement', required: false, type: 'numeric', category: 'general' },
  { id: 'remarks', labelKey: 'catalog.qc.checklist.default.remarks', required: false, type: 'text', category: 'general' }
];

export const DEFAULT_QC_LAB_ITEMS: QcChecklistDefaultItem[] = [
  { id: 'lab_microbiology', labelKey: 'catalog.qc.checklist.lab.microbiology', required: true, type: 'lab_test', category: 'laboratory', methodHintKey: 'catalog.qc.checklist.lab.method.microbiology' },
  { id: 'lab_serology', labelKey: 'catalog.qc.checklist.lab.serology', required: true, type: 'lab_test', category: 'laboratory', methodHintKey: 'catalog.qc.checklist.lab.method.serology' },
  { id: 'lab_chemical', labelKey: 'catalog.qc.checklist.lab.chemical', required: true, type: 'lab_test', category: 'laboratory', methodHintKey: 'catalog.qc.checklist.lab.method.chemical' },
  { id: 'lab_uv', labelKey: 'catalog.qc.checklist.lab.uv', required: true, type: 'lab_test', category: 'laboratory', methodHintKey: 'catalog.qc.checklist.lab.method.uv' },
  { id: 'lab_physical', labelKey: 'catalog.qc.checklist.lab.physical', required: false, type: 'lab_test', category: 'laboratory', methodHintKey: 'catalog.qc.checklist.lab.method.physical' },
  { id: 'lab_heavy_metals', labelKey: 'catalog.qc.checklist.lab.heavyMetals', required: false, type: 'lab_test', category: 'laboratory', methodHintKey: 'catalog.qc.checklist.lab.method.heavyMetals' },
  { id: 'lab_moisture', labelKey: 'catalog.qc.checklist.lab.moisture', required: false, type: 'lab_test', category: 'laboratory', unit: '%', methodHintKey: 'catalog.qc.checklist.lab.method.moisture' },
  { id: 'lab_purity', labelKey: 'catalog.qc.checklist.lab.purity', required: false, type: 'lab_test', category: 'laboratory', unit: '%', methodHintKey: 'catalog.qc.checklist.lab.method.purity' },
  { id: 'lab_ph', labelKey: 'catalog.qc.checklist.lab.ph', required: false, type: 'lab_test', category: 'laboratory', methodHintKey: 'catalog.qc.checklist.lab.method.ph' },
  { id: 'lab_endotoxin', labelKey: 'catalog.qc.checklist.lab.endotoxin', required: false, type: 'lab_test', category: 'laboratory', unit: 'EU/mL', methodHintKey: 'catalog.qc.checklist.lab.method.endotoxin' },
  { id: 'lab_dissolution', labelKey: 'catalog.qc.checklist.lab.dissolution', required: false, type: 'lab_test', category: 'laboratory', unit: '%', methodHintKey: 'catalog.qc.checklist.lab.method.dissolution' },
  { id: 'lab_sterility', labelKey: 'catalog.qc.checklist.lab.sterility', required: false, type: 'lab_test', category: 'laboratory', methodHintKey: 'catalog.qc.checklist.lab.method.sterility' }
];

export const DEFAULT_QC_CHECKLIST_ITEMS: QcChecklistDefaultItem[] = [
  ...DEFAULT_QC_GENERAL_ITEMS,
  ...DEFAULT_QC_LAB_ITEMS
];

export function createEmptyLabTestValue(unit = ''): QcLabTestValue {
  return {
    sampleReference: '',
    method: '',
    resultValue: '',
    unit,
    referenceRange: '',
    labReportReference: '',
    testedAt: null
  };
}

export function buildDefaultChecklistTemplate(translate: (key: string) => string): QcChecklistTemplate {
  return {
    version: 1,
    items: DEFAULT_QC_CHECKLIST_ITEMS.map(item => mapDefaultItem(item, translate))
  };
}

export function parseChecklistTemplate(json: string | null | undefined, translate: (key: string) => string): QcChecklistTemplate {
  if (!json?.trim()) {
    return buildDefaultChecklistTemplate(translate);
  }

  try {
    const parsed = JSON.parse(json) as unknown;
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as QcChecklistTemplate).items)) {
      const template = parsed as QcChecklistTemplate;
      const customItems = template.items
        .filter(item => item?.id && item?.label)
        .map(item => ({
          id: String(item.id),
          label: String(item.label),
          required: Boolean(item.required),
          type: normalizeItemType(item.type),
          category: normalizeCategory(item.category, item.type),
          unit: item.unit ? String(item.unit) : undefined,
          minValue: typeof item.minValue === 'number' ? item.minValue : undefined,
          maxValue: typeof item.maxValue === 'number' ? item.maxValue : undefined,
          methodHintKey: item.methodHintKey ? String(item.methodHintKey) : undefined
        }));

      if (customItems.some(item => item.category === 'laboratory' || item.type === 'lab_test')) {
        return { version: 1, items: customItems };
      }

      return {
        version: 1,
        items: [...customItems, ...DEFAULT_QC_LAB_ITEMS.map(item => mapDefaultItem(item, translate))]
      };
    }

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed as Record<string, unknown>);
      if (entries.length > 0) {
        return {
          version: 1,
          items: [
            ...entries.map(([id, value]) => ({
              id,
              label: humanizeChecklistId(id),
              required: true,
              type: typeof value === 'number' ? 'numeric' as const : 'pass_fail' as const,
              category: id.startsWith('lab_') ? 'laboratory' as const : 'general' as const
            })),
            ...DEFAULT_QC_LAB_ITEMS.map(item => mapDefaultItem(item, translate))
          ]
        };
      }
    }
  } catch {
    // fall through to default template
  }

  return buildDefaultChecklistTemplate(translate);
}

export function parseChecklistResults(
  json: string | null | undefined,
  template: QcChecklistTemplate,
  meta: { ruleId: string | null; ruleCode: string | null }
): QcChecklistResults {
  const baseItems = template.items.map(def => emptyResultFromDef(def));

  if (!json?.trim()) {
    return { version: 1, ruleId: meta.ruleId, ruleCode: meta.ruleCode, items: baseItems };
  }

  try {
    const parsed = JSON.parse(json) as unknown;

    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as QcChecklistResults).items)) {
      const saved = parsed as QcChecklistResults;
      const savedById = new Map(saved.items.map(item => [item.id, item]));
      return {
        version: 1,
        ruleId: saved.ruleId ?? meta.ruleId,
        ruleCode: saved.ruleCode ?? meta.ruleCode,
        items: baseItems.map(item => {
          const existing = savedById.get(item.id);
          return existing ? mergeResult(item, existing) : item;
        })
      };
    }

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const flat = parsed as Record<string, unknown>;
      return {
        version: 1,
        ruleId: meta.ruleId,
        ruleCode: meta.ruleCode,
        items: baseItems.map(item => {
          if (!(item.id in flat)) return item;
          const raw = flat[item.id];
          if (item.type === 'lab_test') {
            return mergeLabFlatResult(item, raw);
          }
          if (item.type === 'numeric' && typeof raw === 'number') {
            return { ...item, value: raw, outcome: deriveNumericOutcome(item, raw) };
          }
          if (typeof raw === 'string') {
            return { ...item, outcome: normalizeOutcome(raw) };
          }
          if (typeof raw === 'boolean') {
            return { ...item, value: raw, outcome: raw ? 'pass' : 'fail' };
          }
          return item;
        })
      };
    }
  } catch {
    // fall through
  }

  return { version: 1, ruleId: meta.ruleId, ruleCode: meta.ruleCode, items: baseItems };
}

export function serializeChecklistResults(results: QcChecklistResults): string {
  return JSON.stringify({
    version: 1,
    ruleId: results.ruleId,
    ruleCode: results.ruleCode,
    items: results.items.map(item => ({
      id: item.id,
      label: item.label,
      type: item.type,
      category: item.category,
      required: item.required,
      unit: item.unit ?? null,
      minValue: item.minValue ?? null,
      maxValue: item.maxValue ?? null,
      methodHintKey: item.methodHintKey ?? null,
      outcome: item.outcome,
      value: item.value ?? null,
      notes: item.notes
    }))
  });
}

export function validateChecklistResults(results: QcChecklistResults): string[] {
  const errors: string[] = [];
  for (const item of results.items) {
    if (!item.required) continue;

    if (item.type === 'pass_fail' && (!item.outcome || item.outcome === 'na')) {
      errors.push(item.id);
      continue;
    }

    if (item.type === 'numeric') {
      const value = item.value;
      if (value === null || value === undefined || value === '') {
        errors.push(item.id);
        continue;
      }
      if (!item.outcome || item.outcome === 'na') {
        errors.push(item.id);
      }
      continue;
    }

    if (item.type === 'text' && !String(item.value ?? '').trim()) {
      errors.push(item.id);
      continue;
    }

    if (item.type === 'boolean' && (item.value === null || item.value === undefined)) {
      errors.push(item.id);
      continue;
    }

    if (item.type === 'lab_test') {
      const lab = normalizeLabValue(item.value, item.unit);
      if (!lab.resultValue.trim() || !item.outcome || item.outcome === 'na') {
        errors.push(item.id);
      }
    }
  }
  return errors;
}

export function groupChecklistItems(items: QcChecklistItemResult[]): Array<{ category: QcChecklistItemCategory; items: QcChecklistItemResult[] }> {
  const general = items.filter(item => item.category !== 'laboratory');
  const laboratory = items.filter(item => item.category === 'laboratory');
  const groups: Array<{ category: QcChecklistItemCategory; items: QcChecklistItemResult[] }> = [];
  if (general.length > 0) groups.push({ category: 'general', items: general });
  if (laboratory.length > 0) groups.push({ category: 'laboratory', items: laboratory });
  return groups;
}

function mapDefaultItem(item: QcChecklistDefaultItem, translate: (key: string) => string): QcChecklistItemDef {
  return {
    id: item.id,
    label: translate(item.labelKey),
    required: item.required,
    type: item.type,
    category: item.category,
    unit: item.unit,
    methodHintKey: item.methodHintKey
  };
}

function emptyResultFromDef(def: QcChecklistItemDef): QcChecklistItemResult {
  const category = def.category ?? (def.type === 'lab_test' ? 'laboratory' : 'general');
  return {
    id: def.id,
    label: def.label,
    type: def.type,
    category,
    required: Boolean(def.required),
    unit: def.unit,
    minValue: def.minValue,
    maxValue: def.maxValue,
    methodHintKey: def.methodHintKey,
    outcome: def.type === 'pass_fail' || def.type === 'lab_test' ? '' : 'na',
    value: def.type === 'boolean'
      ? false
      : def.type === 'text'
        ? ''
        : def.type === 'lab_test'
          ? createEmptyLabTestValue(def.unit ?? '')
          : null,
    notes: ''
  };
}

function mergeResult(base: QcChecklistItemResult, saved: Partial<QcChecklistItemResult>): QcChecklistItemResult {
  const merged: QcChecklistItemResult = {
    ...base,
    label: saved.label ?? base.label,
    type: saved.type ? normalizeItemType(saved.type) : base.type,
    category: saved.category ? normalizeCategory(saved.category, saved.type ?? base.type) : base.category,
    required: saved.required ?? base.required,
    unit: saved.unit ?? base.unit,
    minValue: saved.minValue ?? base.minValue,
    maxValue: saved.maxValue ?? base.maxValue,
    methodHintKey: saved.methodHintKey ?? base.methodHintKey,
    outcome: saved.outcome ? normalizeOutcome(saved.outcome) : base.outcome,
    notes: saved.notes ?? base.notes
  };

  if (merged.type === 'lab_test') {
    merged.value = normalizeLabValue(saved.value, merged.unit);
    return merged;
  }

  merged.value = saved.value ?? base.value;
  return merged;
}

function mergeLabFlatResult(item: QcChecklistItemResult, raw: unknown): QcChecklistItemResult {
  const lab = normalizeLabValue(item.value, item.unit);
  if (typeof raw === 'string') {
    return { ...item, value: { ...lab, resultValue: raw }, outcome: normalizeOutcome(raw) };
  }
  if (raw && typeof raw === 'object') {
    const record = raw as Partial<QcLabTestValue> & { outcome?: unknown };
    return {
      ...item,
      value: normalizeLabValue({ ...lab, ...record }, item.unit),
      outcome: record.outcome ? normalizeOutcome(record.outcome) : item.outcome
    };
  }
  return item;
}

function normalizeLabValue(value: unknown, unit?: string): QcLabTestValue {
  const base = createEmptyLabTestValue(unit ?? '');
  if (!value || typeof value !== 'object') {
    return base;
  }

  const record = value as Partial<QcLabTestValue>;
  return {
    sampleReference: String(record.sampleReference ?? ''),
    method: String(record.method ?? ''),
    resultValue: String(record.resultValue ?? ''),
    unit: String(record.unit ?? base.unit),
    referenceRange: String(record.referenceRange ?? ''),
    labReportReference: String(record.labReportReference ?? ''),
    testedAt: record.testedAt ? String(record.testedAt) : null
  };
}

function normalizeItemType(value: unknown): QcChecklistItemType {
  if (value === 'numeric' || value === 'text' || value === 'boolean' || value === 'lab_test') return value;
  return 'pass_fail';
}

function normalizeCategory(value: unknown, type: unknown): QcChecklistItemCategory {
  if (value === 'laboratory' || value === 'general') return value;
  return type === 'lab_test' || String(value).startsWith('lab_') ? 'laboratory' : 'general';
}

function normalizeOutcome(value: unknown): QcChecklistOutcome {
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'pass' || normalized === 'passed' || normalized === 'ok' || normalized === 'negative') return 'pass';
  if (normalized === 'fail' || normalized === 'failed' || normalized === 'positive') return 'fail';
  if (normalized === 'na' || normalized === 'n/a') return 'na';
  return '';
}

function deriveNumericOutcome(item: QcChecklistItemResult, value: number): QcChecklistOutcome {
  if (item.minValue !== undefined && value < item.minValue) return 'fail';
  if (item.maxValue !== undefined && value > item.maxValue) return 'fail';
  return 'pass';
}

function humanizeChecklistId(id: string): string {
  return id
    .replace(/^lab_/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}
